// Client-side photo downscale, applied at ATTACH time (before the classify
// POST). Load-bearing since pixels stopped shipping to the brain (2026-06-10):
// the classify lane is now the ONLY reader of the photo, Anthropic's per-image
// cap is ~5MB decoded, and Android camera shots run 3-12MB. A VIN sticker
// doesn't need 12MP — Anthropic vision resizes to ~1568px longest edge anyway,
// so 1600px JPEG is at-parity with what the model would see, while cutting the
// upload to ~300-500KB (faster classify, lower vision cost, smaller R2 object).
//
// Failure-safe by design: ANY problem (HEIC the browser can't decode, corrupt
// file, no canvas) returns the ORIGINAL file — the classify endpoint then
// rejects unsupported/oversized input exactly as it does today and the staged
// chip shows the honest "couldn't read" state.

const DEFAULT_MAX_EDGE = 1600
const DEFAULT_QUALITY = 0.8
// Already small enough — skip the re-encode (keeps tiny screenshots/PNGs
// byte-identical and saves the canvas pass).
const SKIP_BYTES = 1_500_000

export async function downscaleImage(
  file: File,
  opts: { maxEdge?: number; quality?: number } = {},
): Promise<File> {
  const maxEdge = opts.maxEdge ?? DEFAULT_MAX_EDGE
  const quality = opts.quality ?? DEFAULT_QUALITY
  try {
    if (!file.type.startsWith('image/')) return file
    // `from-image` applies the EXIF orientation so a portrait phone shot
    // isn't re-encoded sideways.
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
    try {
      const longest = Math.max(bitmap.width, bitmap.height)
      if (longest <= maxEdge && file.size <= SKIP_BYTES) return file
      const scale = Math.min(1, maxEdge / longest)
      const w = Math.max(1, Math.round(bitmap.width * scale))
      const h = Math.max(1, Math.round(bitmap.height * scale))
      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d')
      if (!ctx) return file
      // White fill so transparent PNG regions don't turn black in the JPEG.
      ctx.fillStyle = '#fff'
      ctx.fillRect(0, 0, w, h)
      ctx.drawImage(bitmap, 0, 0, w, h)
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', quality),
      )
      if (!blob) return file
      const name = file.name.replace(/\.[a-z0-9]+$/i, '') || 'photo'
      return new File([blob], `${name}.jpg`, { type: 'image/jpeg' })
    } finally {
      bitmap.close()
    }
  } catch {
    return file
  }
}
